import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  fetchBuildingDashboardConfig,
  fetchBuildingDetails,
  fetchBuildingStats,
  fetchCommentSentimentStats,
  updateHowToGuideConfig,
  triggerPartnerSync,
  triggerPartnerFeedback,
  IRaffle,
  updateBuilding,
  updateBuildingUnits
} from '~/api/building';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import StatsBox from '~/components/StatsBox/StatsBox';
import { Building, BuildingStats } from '~/types/building';
import { connect } from 'react-redux';
import { RootState } from '~/redux/store';
import { ReactComponent as Bullet } from '~/utils/images/bullet.svg';
import classes from './Dashboard.module.css';
import {
  IBuildingConfigColumn,
  configureDashboardData
} from '~/helpers/configureDashboardData';
import Spinner from '~/components/Spinner/Spinner';
import { Mixpanel } from '../../services/MixpanelService';
import { Roles } from '~/enums/Roles';
import { toast } from 'react-toastify';
import { configureCommentSentimentData } from '~/helpers/configureCommentSentimentData';
import { useBuildingKeyAttribute } from '../EditBuildingContainer/EditBuildingKeys/hooks';
import ResourceIcon from '~/utils/images/resources.png';
import Spacer from '~/components/Spacer/Spacer';
import useGetBuildingDetails from '~/hooks/building/useGetBuildingDetails';
import moment from 'moment';
import { useDeeplink } from '~/hooks/deeplinks/useDeeplink';
import { ReactComponent as CopyIcon } from '~/utils/images/copyIcon.svg';
import useRaffle from '~/hooks/building/useRaffle';

import { getActiveTheme, COBU_THEME } from '~/theme/vendorTheme';
import { VENDOR_NAME_KEY } from '~/theme/vendorConstants';

type Props = ReturnType<typeof mapStateToProps>;

const Stats = (props: Props) => {
  const { buildingId } = useParams<{ buildingId: string }>();
  const building: null | Partial<Building> = useGetBuildingDetails(buildingId);
  const { raffle } = useRaffle(buildingId);
  const { userRole } = props;
  const activeTheme = getActiveTheme();
  const isVendorTheme = activeTheme !== COBU_THEME;
  const queryClient = useQueryClient();
  const { buildingKeyAttributes } = useBuildingKeyAttribute(buildingId);
  const history = useHistory();
  const { activeDeepLinks } = useDeeplink(buildingId);

  const onBoardingDeeplink = useMemo(() => {
    if (!activeDeepLinks?.general) {
      return null;
    }

    return activeDeepLinks?.general.find(
      (deeplink) => deeplink.title === 'Onboarding Link'
    );
  }, [activeDeepLinks?.general]);

  const {
    data: buildingDetailsRes,
    isSuccess: isBuildingDetailsSuccess,
    isLoading: isBuildingDetailsLoading,
    isError: isBuildingDetailsError
  } = useQuery(
    ['buildingDetails', buildingId],
    () => fetchBuildingDetails(buildingId),
    {
      refetchOnWindowFocus: false
    }
  );
  let buildingUserOptions: any = [];
  if (isBuildingDetailsSuccess) {
    const activeResidents = buildingDetailsRes?.data.residents.filter(
      (resident: { deactivated: boolean }) => !resident.deactivated
    );
    buildingUserOptions = activeResidents.map((resident: { email: string }) => {
      return { value: resident.email, label: resident.email };
    });
  }

  const {
    data: buildingDashboardRes,
    isLoading: isBuildingDashboardLoading,
    isError: isBuildingDashboardError
  } = useQuery(
    ['buildingDashboardConfig', buildingId],
    () => fetchBuildingDashboardConfig(buildingId),
    {
      refetchOnWindowFocus: false
    }
  );

  const isVcmOrSuperadmin =
    userRole === Roles.Vcm || userRole === Roles.Superadmin;

  const isVendorMode = !!sessionStorage.getItem(VENDOR_NAME_KEY);

  const [isEditingHowToLink, setIsEditingHowToLink] = useState(false);
  const [howToLinkInput, setHowToLinkInput] = useState('');

  const updateHowToLinkMutation = useMutation(
    (link: string | null) => updateHowToGuideConfig(buildingId, link),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['buildingDashboardConfig', buildingId]);
        setIsEditingHowToLink(false);
        toast('How to Guide link updated!');
      },
      onError: () => {
        toast('Something went wrong. Please try again.');
      }
    }
  );

  const partnerSyncMutation = useMutation(() => triggerPartnerSync(buildingId), {
    onSuccess: (res) => {
      const { events, rsvps } = res.data;
      toast(`Sync complete — events synced: ${events.totalSynced}, RSVPs synced: ${rsvps.totalSynced}`);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || 'Sync failed. Check logs.';
      toast(msg);
    },
  });

  const partnerFeedbackMutation = useMutation(() => triggerPartnerFeedback(buildingId), {
    onSuccess: (res) => {
      const { totalCreated, totalSkipped } = res.data;
      toast(`Feedback triggered — created: ${totalCreated}, already existed: ${totalSkipped}`);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || 'Feedback trigger failed. Check logs.';
      toast(msg);
    },
  });

  const showSentimentData = () => {
    return (
      ((userRole === Roles.Pm || userRole === Roles.PM) &&
        buildingDashboardRes?.data.config.showSentimentDataToPM) ||
      userRole === Roles.Vcm ||
      userRole === Roles.Superadmin
    );
  };

  const {
    data: statsRes,
    isLoading: isBuildingStatsLoading,
    isError: isBuildingStatsError
  } = useQuery(
    ['buildingStats', buildingId],
    () => fetchBuildingStats(buildingId),
    {
      refetchOnWindowFocus: false
    }
  );
  const stats = statsRes?.data;
  // Homebody Premium residents use the Homebody app, so the Cobu invite message and link don't apply.
  const isHomebodyPremium =
    !!stats?.isHomebody && buildingDashboardRes?.data.config.isPaid !== false;
  const shouldShowJumpStartCTA =
    !stats?.isVendorMode && !isVendorMode && !isHomebodyPremium;

  const commentSentimentQuery = useQuery(
    ['commentSentiment', buildingId],
    () => fetchCommentSentimentStats(buildingId),
    {
      refetchOnWindowFocus: false,
      enabled: !isBuildingDashboardLoading && showSentimentData()
    }
  );

  if (
    isBuildingDetailsError ||
    isBuildingDashboardError ||
    isBuildingStatsError ||
    commentSentimentQuery.isError
  ) {
    toast('Something went wrong. Please try again.');
  }

  const updateStats = async (totalOccupiedUnits: number) => {
    try {
      let resp = await updateBuildingUnits({ totalOccupiedUnits }, buildingId);
      const copyStats = stats;
      queryClient.setQueryData(['buildingStats', buildingId], {
        data: {
          ...copyStats,
          totalOccupiedUnits: resp.data.totalOccupiedUnits
        }
      });
      toast('Occupied units have been updated!');
    } catch (error) {
      console.log(error);
      toast('Something went wrong! Please try again later.');
    }
  };
  useEffect(() => {
    if (building) {
      Mixpanel.track('did_open_building_dashboard', {
        $building_name: (building as Building).name
      });
    }
  });

  const changeStats = (email: string) => {
    let copyStats: BuildingStats = { ...stats };
    if (userRole === Roles.Superadmin || userRole === Roles.Vcm)
      copyStats.VCMCommenter = email;
    else copyStats.PMCommenter = email;

    queryClient.setQueryData(['buildingStats', buildingId], {
      data: copyStats
    });
  };

  const renderConfigColumn = (data: IBuildingConfigColumn) => {
    const siblingContent = data.sibling ? (
      <div className='margin-bottom-16'>
        {data.sibling.map((e) => renderConfigColumn(e))}
      </div>
    ) : null;

    const hasVisibleCards = Object.values(data.cards).some(
      (card) =>
        card.show &&
        (card.selector !== 'neighborhoodOffers' ||
          buildingDashboardRes?.data.config.showPerks)
    );

    return (
      <div key={data.title} className={`${classes.column}  margin-right-40`}>
        {!data.appendSiblingAtBottom && siblingContent}
        {hasVisibleCards && (
          <div className={classes.subtitleContainer}>
            <Bullet fill={data.color} />
            <div
              className={classes.subtitle}
              style={{ color: activeTheme.pageHeaderColor }}
            >
              {data.title}
            </div>
          </div>
        )}
        {Object.values(data.cards)
          .filter(
            (card) =>
              card.selector !== 'neighborhoodOffers' ||
              buildingDashboardRes?.data.config.showPerks
          )
          .map((card) => {
            return (
              <StatsBox
                key={card.selector}
                title={card.title}
                description={card.description}
                statsValue={card.statsValue}
                color={data.color}
                footerLinkText={card.footerLinkText}
                footerLink={card.footerLink}
                renderCustomFooter={card.renderCustomFooter}
                titleLink={card.titleLink}
                action={card.action}
                building={buildingDetailsRes?.data}
                currentUserRole={userRole}
                changeStats={changeStats}
                show={card.show}
                showActions={card.showAction}
                buildingUserOptions={buildingUserOptions}
                updateOccupiedUnits={updateStats}
                styles={card.styles}
              />
            );
          })}
        {data.appendSiblingAtBottom && siblingContent}
      </div>
    );
  };

  const googleDriveLink = useMemo(() => {
    if (!buildingKeyAttributes) {
      return null;
    }

    const googleDriveAttribute =
      buildingKeyAttributes?.attributedBuildingKeyValue.find((atttribute) =>
        atttribute.name.includes('Google Drive')
      );

    if (googleDriveAttribute) {
      return googleDriveAttribute.value;
    }

    return null;
  }, [buildingKeyAttributes]);

  const isOnboardingCompleted = useMemo(() => {
    if (!buildingKeyAttributes) {
      return true;
    }

    const googleDriveAttribute =
      buildingKeyAttributes?.attributedBuildingKeyValue.find((atttribute) =>
        atttribute.name.includes('Property Onboarded')
      );

    if (googleDriveAttribute) {
      return googleDriveAttribute.value === '1';
    }

    return false;
  }, [buildingKeyAttributes]);

  const onOnboarding = () => {
    history.push(`/building/${buildingId}/on-boarding`);
  };

  const showJoinAppMessage =
    raffle?.amount &&
    raffle?.endDate &&
    buildingDetailsRes?.data.buildingGoLive &&
    moment(new Date()).diff(buildingDetailsRes.data.buildingGoLive, 'days') <=
      30 &&
    moment(new Date()).diff(buildingDetailsRes.data.buildingGoLive, 'days') >=
      0;

  const copyJumpStartLink = () => {
    navigator.clipboard.writeText(
      `${
        showJoinAppMessage
          ? `Join Cobu & win $${
              raffle?.amount
            }! Post your fav restaurant by ${moment(raffle?.endDate).format(
              'M/DD'
            )} 🎉`
          : `Connect with your neighbors on Cobu! A community app made for ${
              (building as unknown as Building).name
            }}`
      }
${onBoardingDeeplink?.link}`
    );

    toast('Link copied to clipboard!');
  };

  const showGoogleDriveAnchor =
    userRole === Roles.Vcm || userRole === Roles.Superadmin;

  const showAiVisibilityCta = !!stats && !stats.hasAiVisibility;

  const hasRetentionSummary = !!(
    stats?.totalRetainedLeases
  );

  return (
    <div>
      {showAiVisibilityCta && (
        <div className={classes.aiVisibilityBanner}>
          <img
            src='https://framerusercontent.com/images/dINqF5M5ZEBh7HKVhvihYYIqPo.png'
            alt='CobuAI'
            width={36}
            height={36}
            style={{ borderRadius: 8, marginBottom: 8, display: 'block' }}
          />
          1 in 2 renters now use AI to find their apartment (J Turner, August
          2026). CobuAI turns your community's everyday activity on Cobu into
          content that helps your community show up when renters search with
          AI.{' '}
          <a
            href='https://www.cobuai.com/demo'
            target='_blank'
            rel='noopener noreferrer'
          >
            See how CobuAI works
          </a>{' '}
          or email{' '}
          <a href='mailto:customersuccess@livecobu.com'>
            customersuccess@livecobu.com
          </a>
          .
        </div>
      )}
      {showGoogleDriveAnchor && !isOnboardingCompleted && (
        <div>
          <div className={classes.onBoardingContainer}>
            <div className={classes.onBoardingRightContent}>
              <div className={`bold ${classes.onBoardingTitle}`}>
                ONBOARDING IN PROGRESS...
              </div>
              <a
                href={
                  'https://docs.google.com/spreadsheets/d/12KFsM7-jtXndPY5CW_SCyL2WIbjTWbcnwVMaNtNbqdM/edit?gid=0#gid=0'
                }
                target='_blank'
                rel='noopener noreferrer'
              >
                <button className='btn-standard btn-primary'>
                  View Status
                </button>
              </a>
            </div>
            <button
              onClick={onOnboarding}
              className={`btn-standard btn-secondary ${classes.completeOnBoarding}`}
            >
              Complete Onboarding
            </button>
          </div>
          <Spacer height={30} />
        </div>
      )}
      {!isBuildingDashboardLoading &&
      !isBuildingDetailsLoading &&
      !isBuildingStatsLoading &&
      !commentSentimentQuery.isLoading ? (
        <div className='content-container' style={{ backgroundColor: activeTheme.pageBackground || undefined }}>
          {shouldShowJumpStartCTA && (
            <div className={classes.boxContainer}>
              <div className={classes.boxContent}>
                <div className={classes.jumpStartContainer}>
                  <div>
                    <div className={classes.jumpStartHeading}>
                        Jump-Start Your Community's Engagement!
                    </div>
                    <div className={classes.jumpStartSubHeading}>
                      Sending an SMS message or email to all of your residents can quickly
                      jump-start your community’s engagement
                    </div>
                  </div>
                  <div className={classes.jumpStartLinkContainer}>
                    <div
                      className={`${classes.copyJumpStart} cursor-pointer`}
                      onClick={copyJumpStartLink}
                    >
                      <CopyIcon width={20} height={20} />
                      {` Copy`}
                    </div>
                    <div className={classes.jumpStartLink}>
                      <div>
                        {showJoinAppMessage
                          ? `Join Cobu & win $${
                              raffle?.amount
                            }! Post your fav restaurant by ${moment(
                              raffle?.endDate
                            ).format('M/DD')} 🎉`
                          : `Connect with your neighbors on Cobu! A community app made for ${
                              (building as unknown as Building).name
                            }`}
                      </div>
                      <div>{onBoardingDeeplink?.link}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <Breadcrumbs />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end'
            }}
          >
            <div className='margin-top-24'>
              <h2>
                {building ? (building as Building).name : ''}
                {''}
              </h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginRight: '40px' }}>
              {userRole === Roles.Superadmin && buildingId === '5059cc4e-cc7a-4f13-a1d4-f5086ea5367b' && (
                <>
                  <button
                    className='btn-standard btn-secondary'
                    onClick={() => partnerSyncMutation.mutate()}
                    disabled={partnerSyncMutation.isLoading}
                  >
                    {partnerSyncMutation.isLoading ? 'Syncing...' : 'Re-mock Partner Events'}
                  </button>
                  <button
                    className='btn-standard btn-secondary'
                    onClick={() => partnerFeedbackMutation.mutate()}
                    disabled={partnerFeedbackMutation.isLoading}
                  >
                    {partnerFeedbackMutation.isLoading ? 'Triggering...' : 'Trigger Event Feedback'}
                  </button>
                </>
              )}
              {isVcmOrSuperadmin && (
                isEditingHowToLink ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type='text'
                      value={howToLinkInput}
                      onChange={(e) => setHowToLinkInput(e.target.value)}
                      placeholder='https://...'
                      className={classes.howToGuideLinkInput}
                      autoFocus
                    />
                    <button
                      className='btn-standard btn-primary'
                      onClick={() => updateHowToLinkMutation.mutate(howToLinkInput || null)}
                      disabled={updateHowToLinkMutation.isLoading}
                    >
                      Save
                    </button>
                    <button
                      className='btn-standard btn-secondary'
                      onClick={() => setIsEditingHowToLink(false)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {buildingDashboardRes?.data.howToGuideLink && (
                      <a
                        href={buildingDashboardRes.data.howToGuideLink}
                        target='_blank'
                        rel='noreferrer'
                        className={classes.howToGuideLink}
                      >
                        How to Guide
                      </a>
                    )}
                    <button
                      className='btn-standard btn-secondary'
                      onClick={() => {
                        setHowToLinkInput(buildingDashboardRes?.data.howToGuideLink ?? '');
                        setIsEditingHowToLink(true);
                      }}
                    >
                      {buildingDashboardRes?.data.howToGuideLink ? 'Edit Link' : 'Add How to Guide Link'}
                    </button>
                  </div>
                )
              )}
              {isVendorMode && !isVcmOrSuperadmin && buildingDashboardRes?.data.howToGuideLink && (
                <a
                  href={buildingDashboardRes.data.howToGuideLink}
                  target='_blank'
                  rel='noreferrer'
                  className={classes.howToGuideLink}
                >
                  How to Guide
                </a>
              )}
              {showGoogleDriveAnchor && googleDriveLink && (
                <div className='margin-right-40'>
                  <a href={googleDriveLink} target='_blank'>
                    <img src={ResourceIcon} alt='Google Drive Link' />
                  </a>
                </div>
              )}
            </div>
          </div>
          {hasRetentionSummary && (
            <>
              <div className={`${classes.subtitleContainer} margin-top-24`}>
                <div className={classes.subtitle}>COBU IMPACT &amp; ROI</div>
              </div>
              <div className={classes.container}>
                <div className={`${classes.column} margin-right-40`}>
                  <StatsBox
                    title='Retained Leases (Annualized)'
                    statsValue={(stats?.totalRetainedLeases ?? 0).toString()}
                    color={activeTheme.tealAccent || '#00C2CB'}
                    building={buildingDetailsRes?.data}
                    currentUserRole={userRole}
                    footerLinkText='See Results'
                    footerLink={`/building/${buildingId}/retained-leases`}
                    show={true}
                  />
                </div>
              </div>
            </>
          )}
          {!isVendorMode && (userRole === Roles.Pm ||
            userRole === Roles.PM_FREE ||
            userRole === Roles.Vcm ||
            userRole === Roles.Superadmin) && (
            <>
              <div className={`${classes.subtitleContainer} margin-top-32`}>
                <Bullet fill={activeTheme.darkBlue} />
                <div
                  className={classes.subtitle}
                  style={{ color: activeTheme.pageHeaderColor }}
                >
                  ADD EVENTS
                </div>
              </div>
              <div className={classes.eventsContainer}>
                <div className={classes.eventsContent}>
                  <div className={classes.eventsLeftContent}>
                    <div
                      className={classes.eventsHeading}
                      style={{ color: isVendorTheme ? activeTheme.pageHeaderColor : '#a031e4' }}
                    >
                      Create New Event
                    </div>
                    <div className={classes.eventsSubHeading}>
                      Set up a new event for your residents.
                    </div>
                  </div>
                  <div className={classes.eventsButtonsContainer}>
                    <button
                      className={classes.eventsButton}
                      onClick={() =>
                        history.push(`/building/${buildingId}/gathering/create`)
                      }
                    >
                      Add New Event
                    </button>
                    <button
                      className={classes.eventsButton}
                      onClick={() =>
                        history.push(`/building/${buildingId}/gathering`, {
                          tab: 'templates'
                        })
                      }
                    >
                      Add From Template
                    </button>
                    <button
                      className={classes.eventsButton}
                      onClick={() =>
                        history.push(`/building/${buildingId}/gathering`, {
                          tab: 'past'
                        })
                      }
                    >
                      Duplicate Event
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
          {showSentimentData() && (
            <div className={`${classes.subtitleContainer} margin-top-32`}>
              <div
                className={classes.subtitle}
                style={{ color: activeTheme.pageHeaderColor }}
              >
                SENTIMENT VIEWS
              </div>
            </div>
          )}
          <div>
            <div>
              <div className={classes.container}>
                {commentSentimentQuery.isSuccess &&
                  configureCommentSentimentData(
                    commentSentimentQuery.data.data,
                    buildingId
                  ).map((data) => (
                    <div
                      key={data.title}
                      className={`${classes.column}  margin-right-40`}
                    >
                      <StatsBox
                        title={data.title}
                        description='All comments in 30 days'
                        statsValue={data.statsValue}
                        color={data.color}
                        footerLinkText='See Comments'
                        building={building as unknown as Building}
                        currentUserRole={userRole}
                        show={showSentimentData()}
                        isSentiment
                        footerLink={data.footerLink}
                      />
                    </div>
                  ))}
              </div>
            </div>
            <div className={classes.container}>
              {stats &&
                buildingDashboardRes &&
                configureDashboardData(
                  stats,
                  buildingDetailsRes?.data,
                  userRole,
                  buildingDashboardRes?.data.config
                ).map(renderConfigColumn)}
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.spinner}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

function mapStateToProps(state: RootState) {
  return {
    userRole: state.auth.userRole
  };
}

export default connect(mapStateToProps)(Stats);
